import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { deleteMedia, uploadAudio } from '@/features/orders/ordering/services/orderMediaService.js';
import { v4 as uuidv4 } from 'uuid';
import {
  ALL_TABLE_TYPES,
  UPLOAD_OR_REPLACE_TYPES,
} from '@/constants/constants.js';
import MediaTrimmer from './audioComponents/MediaTrimmer.js';
import LoadingIndicator from '@/components/LoadingIndicator.js';

const AudioUpload = ({
  audio_type,
  client_id,
  label,
  accept,
  onUpload,
  onDelete,
  onReplace,
  onUpdateDescription,
  files = [],
  descriptions = [],
  isDisabled,
  descriptionError,
  required = true,
  onlyUpload = false,
}) => {
  const user_id = sessionStorage.getItem('userid');
  const [audio, setAudio] = useState(
    files.length > 0
      ? files
      : [
          {
            id: `${client_id}-${uuidv4()}`,
            content: null,
            description: '',
          },
        ]
  );
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);
  const fileInputRefs = useRef([]);
  const audioRefs = useRef([]);

  const [isEditing, setIsEditing] = useState(false);
  const [fileToEdit, setFileToEdit] = useState(null);
  const [editType, setEditType] = useState(null); // 'upload' or 'replace'
  const [replaceIndex, setReplaceIndex] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // const getAudioDuration = useCallback((file) => {
  //   return new Promise((resolve, reject) => {
  //     const audio = new Audio();
  //     audio.src = URL.createObjectURL(file);
  //     audio.onloadedmetadata = () => {
  //       if (!isNaN(audio.duration)) {
  //         resolve(audio.duration);
  //       } else {
  //         reject('无法获取音频时长');
  //       }
  //     };
  //     audio.onerror = () => reject('音频文件加载失败');
  //   });
  // }, []);

  // const validateCroppedAudioDuration = useCallback(
  //   async (file) => {
  //     try {
  //       const duration = await getAudioDuration(file);
  //       if (duration > 15) {
  //         return { valid: false, error: '·音频时长不能超过 15 秒! ' };
  //       }
  //       return { valid: true };
  //     } catch (err) {
  //       return {
  //         valid: false,
  //         error: '无法获取音频时长，请使用 MP3/WAV 等格式! ',
  //       };
  //     }
  //   },
  //   [getAudioDuration]
  // );

  const uploadFile = useCallback(
    async (file, client_id, uploadOrReplace) => {
      const formData = new FormData();
      formData.append('id', audio[0].id);
      formData.append('user_id', user_id);
      formData.append('audio_type', audio_type);
      if (client_id) formData.append('client_id', client_id);
      formData.append('audio_file', file);

      const {
        data: { audioUrls },
      } = await uploadAudio(formData, uploadOrReplace);
      return audioUrls;
    },
    [audio, audio_type, user_id]
  );

  const handleFileUpload = (event) => {
    const newFiles = Array.from(event.target.files);
    if (newFiles.length > 0) {
      const file = newFiles[0];

      let regex;
      if (file.type.startsWith('video/')) {
        regex = /\.(mp4|mov|avi)$/i;
      } else if (file.type.startsWith('audio/')) {
        regex = /\.(mp3|wav|ogg|m4a|flac|aac)$/i;
      } else {
        setErrorMessage('文件类型仅支持视频或音频格式！');
        return;
      }

      const isValidFormat = regex.test(file.name);
      if (!isValidFormat) {
        setErrorMessage(
          file.type.startsWith('video/')
            ? '视频格式要求: MP4、MOV、AVI! '
            : '音频格式要求: MP3、WAV、OGG、M4A、FLAC、AAC! '
        );
        return;
      }
      setFileToEdit(file);
      setEditType('upload');
      setIsEditing(true);
    }
  };

  const handleFileReplace = (event, index) => {
    const newFiles = Array.from(event.target.files);
    if (newFiles.length > 0) {
      setFileToEdit(newFiles[0]);
      setEditType('replace');
      setReplaceIndex(index);
      setIsEditing(true);
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    try {
      setIsUploading(true);
      const ossUrl = await uploadFile(
        croppedBlob,
        client_id,
        editType === 'upload'
          ? UPLOAD_OR_REPLACE_TYPES.upload
          : UPLOAD_OR_REPLACE_TYPES.replace
      );

      const updatedAudio = [
        {
          ...audio[0],
          content: ossUrl,
          description: '',
        },
      ];
      setAudio(updatedAudio);
      setErrorMessage('');
      if (editType === 'upload') {
        onUpload(updatedAudio);
      } else if (editType === 'replace') {
        onReplace(updatedAudio, replaceIndex);
      }
    } catch (error) {
      console.error('上传失败', error);
      setErrorMessage('上传失败，请稍后重试');
    } finally {
      setIsEditing(false);
      setIsUploading(false);
    }
  };

  const handleFileDelete = async (id) => {
    try {
      await deleteMedia(ALL_TABLE_TYPES.audio, id);
      const resetAudio = [
        {
          id: `${client_id}-${uuidv4()}`,
          content: null,
          description: '',
        },
      ];
      setAudio(resetAudio);
      onDelete([]);
    } catch {
      console.error('文件删除失败');
    }
  };

  const handleUpdateDescription = (e) => {
    const newAudio = [
      {
        ...audio[0],
        description: e.target.value,
      },
    ];
    setAudio(newAudio);
    onUpdateDescription(newAudio);
  };

  const fileItems = useMemo(
    () =>
      audio.map(
        (_audio, index) =>
          _audio.content && (
            <div key={index} className={`file-item audio`}>
              <div className="file-container">
                <audio
                  ref={(el) => (audioRefs.current[index] = el)}
                  src={
                    typeof _audio.content === 'string'
                      ? _audio.content
                      : URL.createObjectURL(_audio.content)
                  }
                  controls
                  className="audio-player"
                />
                <div className="file-operation-container">
                  <Button
                    className="reupload-Button"
                    onClick={() => fileInputRefs.current[index]?.click()}
                    disabled={isDisabled}
                  >
                    重新上传
                  </Button>
                  <input
                    type="file"
                    accept={accept}
                    style={{ display: 'none' }}
                    ref={(el) => (fileInputRefs.current[index] = el)}
                    onChange={(event) => handleFileReplace(event, index)}
                    disabled={isDisabled}
                  />
                  <Button
                    onClick={() => handleFileDelete(_audio.id)}
                    className="delete-Button"
                    disabled={isDisabled}
                  >
                    删除
                  </Button>
                </div>
              </div>

              <div className="file-description-container">
                <div className={`file-description`}>
                  <div className="description-title-container">
                    {required && (
                      <span style={{ color: 'red', marginRight: '0.25rem' }}>
                        *
                      </span>
                    )}
                    <span className="description-title">声音文本</span>
                  </div>
                  <div className="col-md-8">
                    <input
                      type="text"
                      name={audio_type}
                      className="form-control"
                      placeholder={'添加描述'}
                      defaultValue={_audio.description}
                      onChange={handleUpdateDescription}
                      disabled={isDisabled}
                    />
                    {!descriptions[index] && (
                      <div className="error">{descriptionError}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
      ),
    [
      audio,
      isDisabled,
      accept,
      required,
      handleUpdateDescription,
      descriptions,
      descriptionError,
      handleFileDelete,
    ]
  );

  return (
    <div className="col-md-12">
      <div className="mb-3">
        <div className="audio-upload-section">
          <div className="input-section-container">
            <div className="form-label-container">
              <h5>
                {label} {required && <span style={{ color: 'red' }}> *</span>}
              </h5>
            </div>
            <Button
              type="Button"
              className="add-audio-button"
              onClick={() => fileInputRef.current.click()}
              disabled={isDisabled || files.length > 0}
            >
              新增音频
            </Button>
          </div>

          {files.length > 0 && <div className="file-preview">{fileItems}</div>}

          <div className="file-upload-container">
            {errorMessage && (
              <div className="error-message">{errorMessage}</div>
            )}
            <input
              type="file"
              accept={accept}
              className="form-control"
              multiple
              onChange={handleFileUpload}
              disabled={isDisabled}
              ref={(el) => (fileInputRef.current = el)}
              style={{ display: 'none' }}
            />
            {!onlyUpload && (
              <span style={{ color: 'red' }}>*不上传则默认为音库声音</span>
            )}
          </div>
        </div>
      </div>

      {isEditing &&
        fileToEdit &&
        (isUploading ? (
          <LoadingIndicator
            label={'音频上传中'}
            containerClassName={'flex-row'}
          />
        ) : (
          <MediaTrimmer
            file={fileToEdit}
            onCancel={() => setIsEditing(false)}
            onTrimmed={handleCropComplete}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
          />
        ))}
    </div>
  );
};

export default AudioUpload;
